import { cn } from "@/lib/utils";
import { VoteOption } from "@/types/vote";
import { CheckCircle, CheckIcon, CircleCheckIcon } from "lucide-react";

import { PercentageProgressBar } from "./PercentageProgressBar";

interface VoteResultsProps {
    options: VoteOption[];
    percentages: [number, number];
    selectedOption: number | null;
    firstImage?: string | null;
    secondImage?: string | null;
}

export const imageUnselectedClassNames =
    "h-[240px] w-[420px] rounded-xl object-cover";

export const VoteResults: React.FC<VoteResultsProps> = ({
    options,
    percentages,
    selectedOption,
    firstImage,
    secondImage
}) => {
    const hasImages = firstImage || secondImage;
    const imageSelectedBorderClassNames =
        "border-4 border-blue-400 dark:border-blue-400";
    const imageSelectedIconClassNames = "size-5 stroke-white stroke-2";

    return (
        <div className="flex flex-col gap-4">
            {/* 이미지 결과 (있는 경우) */}
            {hasImages && (
                <div className="mb-2 grid grid-cols-2 gap-4">
                    {/* 첫 번째 옵션 이미지 */}
                    {options[0] && (
                        <div className="relative overflow-hidden rounded-lg">
                            {firstImage ? (
                                <>
                                    <img
                                        src={firstImage}
                                        alt={options[0].text}
                                        className={cn(
                                            imageUnselectedClassNames,
                                            selectedOption === 0 &&
                                                imageSelectedBorderClassNames
                                        )}
                                    />
                                    {/* 선택된 옵션에 CheckCircle 아이콘 표시 */}
                                    {selectedOption === 0 && (
                                        <div className="absolute right-3 top-3 rounded-full bg-blue-400 p-1">
                                            <CheckIcon
                                                className={
                                                    imageSelectedIconClassNames
                                                }
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex aspect-square items-center justify-center bg-gray-100 dark:bg-gray-900">
                                    <span className="text-sm text-gray-400">
                                        이미지 없음
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 두 번째 옵션 이미지 */}
                    {options[1] && (
                        <div className="relative overflow-hidden rounded-lg">
                            {secondImage ? (
                                <>
                                    <img
                                        src={secondImage}
                                        alt={options[1].text}
                                        className={cn(
                                            imageUnselectedClassNames,
                                            selectedOption === 1 &&
                                                imageSelectedBorderClassNames
                                        )}
                                    />
                                    {/* 선택된 옵션에 CheckCircle 아이콘 표시 */}
                                    {selectedOption === 1 && (
                                        <div className="absolute right-3 top-3 rounded-full bg-blue-400 p-1">
                                            <CheckIcon
                                                className={
                                                    imageSelectedIconClassNames
                                                }
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex aspect-square items-center justify-center bg-gray-100 dark:bg-gray-900">
                                    <span className="text-sm text-gray-400">
                                        이미지 없음
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* 퍼센트 진행바 */}
            <div className="flex flex-col gap-2.5">
                {options.map((option, index) => {
                    const isSelected = selectedOption === index;
                    const percentage = percentages[index]!; // Safe: votes always have exactly 2 options

                    return (
                        <PercentageProgressBar
                            key={option.id}
                            text={option.text}
                            percentage={percentage}
                            isSelected={isSelected}
                        />
                    );
                })}
            </div>
        </div>
    );
};
